import { AsyncPipe, CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from '@angular/material/dialog';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject, take, takeUntil } from 'rxjs';
import { Group } from '../../../../models/group.model';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { SchoolRoles } from '../../../../models/roles.school.model';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { SchoolMemberService } from '../../../../services/member.school.service';
import { SchoolRolesService } from '../../../../services/school-roles.service';
import { UtilsService } from '../../../../services/utils.service';
import * as memberActions from '../../../../state/actions/member.school.actions';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import * as schoolMemberSelector from '../../../../state/selectors/member.school.selector';
import * as paginationSelector from '../../../../state/selectors/pagination.selector';
import { BtnChipComponent } from '../../../components/buttons/btn-chip/btn-chip.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnMoreComponent } from '../../../components/buttons/btn-more/btn-more.component';
import { ListUtilsBarComponent } from '../../../components/list-utils-bar/list-utils-bar.component';
import { LoadingMemberComponent } from '../../../components/loaders/loading-member/loading-member.component';
import { DlgAssignRolesComponent } from '../../dlg-assign-roles/dlg-assign-roles.component';
import { ChangeMemberStatusComponent } from '../change-member-status/change-member-status.component';

@Component({
  selector: 'app-list-alumni',
  standalone: true,
  imports: [
    AsyncPipe,
    InitialsPipe,
    CommonModule,
    RouterLink,
    MatDialogModule,
    BtnMoreComponent,
    BtnFabComponent,
    ListUtilsBarComponent,
    ChangeMemberStatusComponent,
    BtnChipComponent,
    LoadingMemberComponent,
  ],
  templateUrl: './list-alumni.component.html',
  styleUrl: './list-alumni.component.css',
})
export class ListAlumniComponent implements OnInit, AfterViewInit, OnDestroy {
  firstNewItemId: string | null = null;
  private cdr = inject(ChangeDetectorRef);
  @ViewChildren('itemRef') itemRefs!: QueryList<ElementRef>;

  route: ActivatedRoute = inject(ActivatedRoute);
  roles: SchoolRolesService = inject(SchoolRolesService);
  memberService: SchoolMemberService = inject(SchoolMemberService);
  toastr: ToastrService = inject(ToastrService);
  private logger: NGXLogger = inject(NGXLogger);
  private dialog: MatDialog = inject(MatDialog);

  members$: Observable<SchoolMember[] | null | undefined> = new Observable();

  loading$: Observable<boolean> = new Observable();
  isSearching$: Observable<boolean> = new Observable();
  showingSearchResults$: Observable<boolean> = new Observable();

  groupId: string | null = null;
  group: Group | null | undefined;

  membershipStatus = MembershipStatus;
  memberType = SchoolMemberType;
  utils = UtilsService;
  rolesType = SchoolRoles;

  exit$: Subject<void> = new Subject<void>();

  constructor(private store: Store<AppState>) {
    this.route.paramMap.subscribe((params) => {
      this.groupId = params.get('id');
    });

    this.loading$ = this.store.select(schoolMemberSelector.loading);

    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
      });

    this.loadStudents();
  }

  ngOnInit() {
    this.members$.pipe(take(1)).subscribe((members) => {
      if (members?.length === 0) {
        this.store.dispatch(
          memberActions.getMembers({
            source: 'ListAlumniComponent :: ngOnInit',
            groupId: this.groupId!,
            status: [MembershipStatus.alumni],
            getMemberType: [SchoolMemberType.student],
          }),
        );
      }
    });
  }

  ngAfterViewInit() {
    this.itemRefs.changes.subscribe(() => {
      if (this.firstNewItemId) {
        const el = this.itemRefs.find(
          (el) =>
            el.nativeElement.getAttribute('data-id') === this.firstNewItemId,
        );
        if (el) {
          el.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });

          requestAnimationFrame(() => {
            this.firstNewItemId = null;
            this.cdr.detectChanges();

            setTimeout(() => {
              this.firstNewItemId = null;
              this.cdr.detectChanges();
            }, 2000);
          });
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onAssignRole(item: SchoolMember) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Update Member Role(s)',
      description: `Select one or more roles to assign to '${item.name}'`,
      action: 'Update Roles',
      member: item,
    };
    const dialogRef = this.dialog.open(DlgAssignRolesComponent, dialogConfig);
  }

  loadStudents() {
    this.members$ = this.store.pipe(
      select(
        schoolMemberSelector.getGroupMembersByType({
          memberType: [SchoolMemberType.student],
          groupId: this.groupId,
          status: [MembershipStatus.alumni],
        }),
      ),
    );
  }

  addMore() {
    this.store
      .select(paginationSelector.getPaginationById(this.groupId!))
      .pipe(take(1))
      .subscribe((lastRecord) => {
        if (lastRecord?.lastStudentRecord?.id) {
          this.firstNewItemId = lastRecord.lastStudentRecord.id;
        }

        this.store.dispatch(
          memberActions.getMembers({
            source: 'ListAlumniComponent :: addMore',
            groupId: this.groupId!,
            status: [MembershipStatus.alumni],
            getMemberType: [SchoolMemberType.student],
            lastRecord: lastRecord,
          }),
        );
      });
  }

  onSearchTermChange(value: string) {
    this.store
      .select(
        memberSelector.getGroupMembersByType({
          memberType: [SchoolMemberType.student],
          groupId: this.groupId,
          status: [MembershipStatus.alumni],
          filter: value,
        }),
      )
      .pipe(take(1))
      .subscribe((items) => {
        this.members$ = of(items);
      });
  }

  async onSearch(term: string) {
    this.members$ = of([]);
    this.isSearching$ = of(true);
    const result = await this.memberService.searchMembers(term, this.groupId!, {
      memberType: [SchoolMemberType.student],
      memberStatus: [MembershipStatus.alumni],
    });
    this.members$ = of(result);
    this.isSearching$ = of(false);
    this.showingSearchResults$ = of(true);
  }

  onCancelSearch() {
    this.loadStudents();
    this.isSearching$ = of(false);
    this.showingSearchResults$ = of(false);
  }

  onRefresh() {
    this.store.dispatch(
      memberActions.getMembers({
        source: 'ListAlumniComponent :: onRefresh',
        groupId: this.groupId!,
        status: [MembershipStatus.alumni],
        getMemberType: [SchoolMemberType.student],
      }),
    );
  }
}
